/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import React from 'react';
import {
    MultiplesColumnIdType,
    MultiplesColumnsType,
    SinglesColumnIdType,
    SinglesColumnsType,
    SinglesVisibleColumnIdsType,
} from './LiabilitiesWidgetTable.types';
import { Amount } from 'src_common/common/amount/Amount';
import { formatPriceForSorting } from './formatPriceForSorting';
import { createSearchParams } from './LiabilitiesWidgetTable.helpers';

export const MultiplesColumns: MultiplesColumnsType = {
    name: {
        label: 'Selection',
        cell: (cell): string => cell.name ?? '-',
        comparatorValueMapper: (cell): string => cell.name ?? '-',
        route: (selection, _currency, eventId): JSX.Element => {
            const params = createSearchParams('multiple', selection, eventId);
            const routePath = `/trading?tab=bet-search&bet.parameters=${params}`;
            return (
                <a
                    href='#'
                    onClick={(e): void => {
                        e.preventDefault();
                        window.open(routePath);
                    }}
                >
                    {selection.name}
                </a>
            );
        },
    },

    prices: {
        label: 'Prices',
        cell: ({ price }): string => price?.f ?? '-',
        comparatorValueMapper: ({ price }): number => price?.d ?? -1,
    },

    totalBets: {
        label: 'Total Legs',
        abbr: 'TL',
        cell: ({ sgl }): number => sgl.totalBetsNum,
        comparatorValueMapper: ({ sgl }): number => sgl.totalBetsNum,
    },
    stakes: {
        label: 'Total Stakes',
        abbr: 'TS',
        cell: ({ sgl }, currency): string =>
            new Amount(sgl.totalStakes).format(currency, { withoutCurrencySymbol: true }),
        comparatorValueMapper: ({ sgl }): string => formatPriceForSorting(100, 100, sgl.totalStakes),
    },
    stakesPer: {
        label: 'Total Stakes %',
        abbr: 'TS% ',
        cell: ({ sgl }): number => sgl.totalStakesPercent,
        comparatorValueMapper: ({ sgl }): number => sgl.totalStakesPercent,
    },
    openBets: {
        label: 'Open Legs',
        abbr: 'OL',
        cell: ({ sgl }): number => sgl.openBetsNum,
        comparatorValueMapper: ({ sgl }): number => sgl.openBetsNum,
    },
    openStakes: {
        label: 'Open Stakes',
        abbr: 'OS',
        cell: ({ sgl }, currency): string =>
            new Amount(sgl.openStakes).format(currency, { withoutCurrencySymbol: true }),
        comparatorValueMapper: ({ sgl }): string => formatPriceForSorting(100, 100, sgl.openStakes),
    },
    payout: {
        label: 'Payout',
        abbr: 'P/O',
        cell: ({ sgl }, currency): string =>
            new Amount(sgl.expPayout).format(currency, { withoutCurrencySymbol: true }),
        comparatorValueMapper: ({ sgl }): string => formatPriceForSorting(100, 100, sgl.expPayout),
    },
    netPayout: {
        label: 'Average net payout',
        abbr: 'ANP',
        cell: ({ sgl }, currency): string =>
            new Amount(sgl.netPayout).format(currency, { withoutCurrencySymbol: true }),
        comparatorValueMapper: ({ sgl }): string => formatPriceForSorting(100, 100, sgl.netPayout),
    },
    rumLines: {
        label: 'Last Legs',
        abbr: 'LL',
        cell: ({ sgl }): number => sgl.rumLines,
        comparatorValueMapper: ({ sgl }): number => sgl.rumLines,
    },
    rumStakes: {
        label: 'Last Legs Stakes',
        abbr: 'LLS',
        cell: ({ sgl }, currency): string =>
            new Amount(sgl.rumStakes).format(currency, { withoutCurrencySymbol: true }),
        comparatorValueMapper: ({ sgl }): string => formatPriceForSorting(100, 100, sgl.rumStakes),
    },
    rumPayout: {
        label: 'Last Legs Payout',
        abbr: 'LLP',
        cell: ({ sgl }, currency): string =>
            new Amount(sgl.rumPayout).format(currency, { withoutCurrencySymbol: true }),
        comparatorValueMapper: ({ sgl }): string => formatPriceForSorting(100, 100, sgl.rumPayout),
    },
};

export const SinglesColumns: SinglesColumnsType = {
    name: {
        label: 'Selection',
        total: (): string => 'Totals:',
        cell: (cell): string => cell.name ?? '-',
        comparatorValueMapper: (cell): string => cell.name ?? '-',
        route: (selection, _currency, eventId): JSX.Element => {
            const params = createSearchParams('single', selection, eventId);
            const routePath = `/trading?tab=bet-search&bet.parameters=${params}`;
            return (
                <a
                    href='#'
                    onClick={(e): void => {
                        e.preventDefault();
                        window.open(routePath);
                    }}
                >
                    {selection.name}
                </a>
            );
        },
    },
    prices: {
        label: 'Prices',
        total: (_total, { overround }): string | null => {
            if (overround === undefined || overround === null) {
                return '-';
            }
            return `${overround}%`;
        },
        cell: ({ price }): string => price?.f ?? '-',
        comparatorValueMapper: ({ price }): number => price?.d ?? -1,
    },
    result: {
        label: 'Result',
        total: (): null => null,
        cell: ({ result }): string =>
            result === undefined || result === null ? '-' : `${result.type}${` (place ${result.place})`}`,
        comparatorValueMapper: ({ result }): number | Record<string, string | number> => {
            if (result === undefined) {
                return 0;
            }
            return 'place' in result ? result.place : result;
        },
    },
    profit: {
        label: 'Profit',
        total: ({ profit }): string => profit,
        cell: ({ sgl }, currency): string => new Amount(sgl.profit).format(currency, { withoutCurrencySymbol: true }),
        comparatorValueMapper: ({ sgl }): string => formatPriceForSorting(100, 100, sgl.profit),
    },
    bets: {
        label: 'Bets',
        total: ({ bets }): number => bets,
        cell: ({ sgl }): number => sgl.bets,
        comparatorValueMapper: ({ sgl }): number => sgl.bets,
    },
    stakes: {
        label: 'Stakes',
        total: ({ stakes }, _liab, currency): string =>
            new Amount(stakes).format(currency, { withoutCurrencySymbol: true }),
        cell: ({ sgl }, currency): string => new Amount(sgl.stakes).format(currency, { withoutCurrencySymbol: true }),
        comparatorValueMapper: ({ sgl }): string => formatPriceForSorting(100, 100, sgl.stakes),
    },
    payout: {
        total: (): null => null,
        label: 'Payout',
        abbr: 'P/O',
        cell: ({ sgl }, currency): string => new Amount(sgl.payout).format(currency, { withoutCurrencySymbol: true }),
        comparatorValueMapper: ({ sgl }): string => formatPriceForSorting(100, 100, sgl.payout),
    },
    placeStakes: {
        label: 'Place stakes',
        abbr: 'PS',
        total: ({ placeStakes }, _liab, currency): string =>
            new Amount(placeStakes).format(currency, { withoutCurrencySymbol: true }),
        cell: ({ sgl }, currency): string =>
            new Amount(sgl.placeStakes).format(currency, { withoutCurrencySymbol: true }),
        comparatorValueMapper: ({ sgl }): string => formatPriceForSorting(100, 100, sgl.placeStakes),
    },
    placePayout: {
        label: 'Place payout',
        abbr: 'PP',
        total: ({ placePayout }): string => placePayout,
        cell: ({ sgl }): number => sgl.placePayout,
        comparatorValueMapper: ({ sgl }): number => sgl.placePayout,
    },
    profitLoss: {
        total: (): null => null,
        label: 'Profit loss',
        abbr: 'P/L%',
        cell: ({ sgl }): string => `${sgl.plRatio}%`,
        comparatorValueMapper: ({ sgl }): number => sgl.plRatio,
    },
    cashOutBets: {
        label: 'Cash out bets',
        abbr: 'COB',
        total: (_total, { sgl }): string => (sgl.totalCashOutBets === '' ? '0' : sgl.totalCashOutBets),
        cell: ({ sgl }): string => (sgl.totalCashOutBets === '' ? '0' : sgl.totalCashOutBets),
        comparatorValueMapper: ({ sgl }): string => sgl.totalCashOutBets,
    },
    cashOutStakes: {
        label: 'Cash out stakes',
        abbr: 'COS',
        total: (_total, { sgl }, currency): string =>
            new Amount(sgl.totalCashOutStakes).format(currency, { withoutCurrencySymbol: true }),
        cell: ({ sgl }, currency): string =>
            new Amount(sgl.totalCashOutStakes).format(currency, { withoutCurrencySymbol: true }),
        comparatorValueMapper: ({ sgl }): string => formatPriceForSorting(100, 100, sgl.totalCashOutStakes),
    },
    cashOutPayout: {
        label: 'Cash out payout',
        abbr: 'COP',
        total: (_total, { sgl }, currency): string =>
            new Amount(sgl.totalCashOutPayout).format(currency, { withoutCurrencySymbol: true }),
        cell: ({ sgl }, currency): string =>
            new Amount(sgl.totalCashOutPayout).format(currency, { withoutCurrencySymbol: true }),
        comparatorValueMapper: ({ sgl }): string => formatPriceForSorting(100, 100, sgl.totalCashOutPayout),
    },
};

export const MULTIPLES_DEFAULT_COLUMN_IDS: Array<MultiplesColumnIdType> = [
    'name',
    'prices',
    'totalBets',
    'stakes',
    'stakesPer',
    'openBets',
    'openStakes',
    'payout',
    'netPayout',
    'rumLines',
    'rumStakes',
    'rumPayout',
];

export const SINGLES_DEFAULT_COLUMN_IDS: Array<SinglesColumnIdType> = [
    'name',
    'prices',
    'result',
    'profit',
    'bets',
    'stakes',
    'payout',
];

export const SINGLES_VISIBLE_COLUMNS: Record<SinglesVisibleColumnIdsType, Array<SinglesColumnIdType>> = {
    default: [...SINGLES_DEFAULT_COLUMN_IDS, 'profitLoss'],
    eachWay: [...SINGLES_DEFAULT_COLUMN_IDS, 'placeStakes', 'placePayout', 'profitLoss'],
    cashout: [...SINGLES_DEFAULT_COLUMN_IDS, 'cashOutBets', 'cashOutStakes', 'cashOutPayout', 'profitLoss'],
    freebet: ['name', 'prices', 'result', 'bets', 'stakes', 'payout', 'profitLoss'],
};
